import { Label } from "@xola/ui-kit";
import clsx from "clsx";
import { isScheduled } from "common";
import pluralize from "pluralize";
import React, { useState } from "react";
import { DemographicPricingGroup, Experience, ExperienceGroup } from "types";
import { getDefaultGroup } from "../../../models/Experience";
import { NumberInlineValuePopover } from "../../Common/NumberInlineValuePopover";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { isLimitAndNotifyGuideHidden } from "./ExperiencePricingFormHelper";

interface GuestLimitSelectorProps {
    experience: Experience & { privatePricing: DemographicPricingGroup };
    errors: any;
    onChange: (value: ExperienceGroup) => void;
}

export const GuestLimitSelector = ({ experience, errors, onChange }: GuestLimitSelectorProps) => {
    const group = experience.group ?? getDefaultGroup();
    const outingMinCutoff = (group.outingMinCutoff ?? 3600) / 60;

    const [isOrderMinPopoverOpen, setIsOrderMinPopoverOpen] = useState(false);
    const [isOrderMaxPopoverOpen, setIsOrderMaxPopoverOpen] = useState(false);
    const [isOutingMinPopoverOpen, setIsOutingMinPopoverOpen] = useState(false);
    const [isOutingMaxPopoverOpen, setIsOutingMaxPopoverOpen] = useState(false);

    const handleChange = (key: string, value: any) => {
        const updatedGroup = { ...group };
        updatedGroup[key as keyof ExperienceGroup] = value;
        onChange(updatedGroup);
    };

    const { privatePricing, allowedPrivacies } = experience;
    const hideLimitAndNotifyGuide = isLimitAndNotifyGuideHidden(allowedPrivacies, privatePricing);

    return (
        <div className="space-y-6">
            {!hideLimitAndNotifyGuide && (
                <div>
                    <Label>Per Reservation Limit</Label>
                    <span className="text-gray-darker">
                        Minimum of
                        <NumberInlineValuePopover
                            isOpen={isOrderMinPopoverOpen}
                            value={group.orderMin}
                            error={errors?.orderMin}
                            min="1"
                            step="1"
                            text={
                                <>
                                    <var data-var="guestCount">{group.orderMin}</var>{" "}
                                    {pluralize("guest", group.orderMin)}
                                </>
                            }
                            onOpenChange={setIsOrderMinPopoverOpen}
                            onChange={(value: number) => handleChange("orderMin", value)}
                        />
                        and maximum of
                        <NumberInlineValuePopover
                            isOpen={isOrderMaxPopoverOpen}
                            value={group.orderMax}
                            error={errors?.orderMax}
                            min="1"
                            step="1"
                            text={
                                group.orderMax ? (
                                    <>
                                        <var data-var="guestCount">{group.orderMax}</var>{" "}
                                        {pluralize("guest", group.orderMax)}
                                    </>
                                ) : (
                                    "no limit"
                                )
                            }
                            onOpenChange={setIsOrderMaxPopoverOpen}
                            onChange={(value: number) => handleChange("orderMax", value ? value : null)}
                        />
                        per reservation
                    </span>
                </div>
            )}
            {isScheduled(experience) && (
                <div className="space-y-2">
                    <Label>Per Event Limit</Label>
                    <span className="text-gray-darker">
                        {hideLimitAndNotifyGuide ? (
                            <>up to</>
                        ) : (
                            <>
                                Minimum of
                                <NumberInlineValuePopover
                                    isOpen={isOutingMinPopoverOpen}
                                    value={group.outingMin}
                                    text={
                                        <>
                                            <var data-var="guestCount">{group.outingMin}</var>{" "}
                                            {pluralize("guest", group.outingMin)}
                                        </>
                                    }
                                    error={errors?.outingMin}
                                    min="1"
                                    step="1"
                                    onOpenChange={setIsOutingMinPopoverOpen}
                                    onChange={(value: number) => handleChange("outingMin", value)}
                                />
                                and maximum of
                            </>
                        )}
                        <NumberInlineValuePopover
                            isOpen={isOutingMaxPopoverOpen}
                            value={group.outingMax}
                            text={
                                group.outingMax ? (
                                    <>
                                        <var data-var="guestCount">{group.outingMax}</var>{" "}
                                        {pluralize("guest", group.outingMax)}
                                    </>
                                ) : (
                                    "no limit"
                                )
                            }
                            error={errors?.outingMax}
                            min="1"
                            step="1"
                            onOpenChange={setIsOutingMaxPopoverOpen}
                            onChange={(value: number) => handleChange("outingMax", value ? value : null)}
                        />
                        per event
                    </span>
                    {!hideLimitAndNotifyGuide && (
                        <div className="text-gray-darker">
                            Notify staff
                            <TimePickerPopover
                                showArrow
                                value={outingMinCutoff}
                                isError={errors?.outingMinCutoff}
                                classNames={{
                                    text: clsx(errors?.outingMinCutoff ? "!text-danger" : "!text-gray-darker", "mx-1"),
                                }}
                                onApply={(value) => handleChange("outingMinCutoff", value * 60)}
                            />
                            prior to start time if the per event minimum has not been met
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
